import { Group, MeshBasicMaterial, type Mesh } from 'three';

import type { CutoutManager } from '../cutout-manager.class';
import type { CutoutInfo } from '../types';
import { createDebugMeshCfn } from './create-debug-mesh.cfn';

export interface CreateDebugVisualizationParams {
	cutouts: CutoutInfo[];
	islands: Group[];
}

/**
 * Create debug visualization meshes from cutout data
 * This should be called AFTER generateCutouts with stored cutout data
 * Debug meshes are added directly to island groups for correct positioning
 */
export const createDebugVisualizationCfn = (
	self: CutoutManager,
	{ cutouts, islands }: CreateDebugVisualizationParams
): { debugGroup: Group; debugMeshes: Mesh[] } => {
	// Retiring stale debug meshes is handled by the caller
	// (CutoutManager.createDebugVisualizationFromData) with per-island scoping.

	// Create a reference group (not used for positioning, just for tracking)
	const debugGroup = new Group();
	debugGroup.name = 'cutout-debug-group';
	debugGroup.visible = self.debugMode;

	const debugMeshes: Mesh[] = [];

	// Index island groups by stable identity so each cutout attaches to the
	// correct island regardless of array/scene order (which drifts after rebuilds).
	const islandsById = new Map<string, Group>();
	for (const island of islands) {
		const id = island.userData?.id as string | undefined;
		if (id) islandsById.set(id, island);
	}

	// Create debug mesh for each cutout and add to the appropriate island
	// This ensures debug meshes are in the same coordinate space as the actual cutouts
	for (const cutout of cutouts) {
		const debugMesh = createDebugMeshCfn({ cutout, debugMode: self.debugMode });
		debugMeshes.push(debugMesh);

		// Attach to the island group by identity (positional fallback for safety)
		const island = (cutout.islandId ? islandsById.get(cutout.islandId) : undefined)
			?? islands[cutout.islandPositionIndex];
		if (island) {
			island.add(debugMesh);
		} else {
			// Fallback: add to debug group if island not found
			debugGroup.add(debugMesh);
		}
	}

	return { debugGroup, debugMeshes };
};

/**
 * Clear all debug visualization meshes
 */
export const clearDebugMeshesCfn = (
	debugMeshes: Mesh[],
	debugGroup: Group | null
): void => {
	for (const mesh of debugMeshes) {
		mesh.geometry.dispose();
		(mesh.material as MeshBasicMaterial).dispose();
		mesh.parent?.remove(mesh);
	}

	if (debugGroup) {
		while (debugGroup.children.length > 0) {
			const child = debugGroup.children[0];
			debugGroup.remove(child);
		}
	}
};
